import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { useIntl } from 'react-intl';
import { IRide, IRideMapMarkers, IRideMapMarkerTooltips } from '../types';
import { RIDE_DETAIL_ZOOM_LEVEL } from '../constants';
import { activeRideNumberAtom, enableWaitingsAtom, selectedRidesAtom } from '../RidePlayerState';
import {
  createWaitingMarkers,
  createWaitingMarkerTooltips,
  hideAllTooltips,
} from '../RidePlayerMarkerService';

interface IUseWaitingMarker {
  waitingMarkers: IRideMapMarkers;
  showHiddenWaitingMarkers: () => void;
  hideShowedWaitingMarkers: () => void;
  showAllWaitingMarkers: () => void;
  hideAllWaitingMarkers: () => void;
}

/**
 * useWaitingMarker custom hook
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @param {number} activeRide
 * @returns {IUseWaitingMarker}
 */
const useWaitingMarker = (
  map: google.maps.Map,
  rides: IRide[],
  // activeRide: number,
  zoomLevel: number
): IUseWaitingMarker => {
  const intl = useIntl();
  const waitingMarkers = React.useRef<IRideMapMarkers>({ 1: [] });
  const waitingMarkerTooltips = React.useRef<IRideMapMarkerTooltips>({ 1: {} });
  const enableWaitings = useRecoilValue<boolean>(enableWaitingsAtom);
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  /**
   * Show hidden waiting markers
   * @returns {void}
   */
  const showHiddenWaitingMarkers = (): void => {
    if (waitingMarkers.current) {
      waitingMarkers.current[activeRide].forEach((marker: google.maps.Marker) => {
        if (!marker.getMap()) {
          marker.setMap(map);
        }
      });
    }
  };

  /**
   * Hide showed waiting markers
   * @returns {void}
   */
  const hideShowedWaitingMarkers = (): void => {
    if (waitingMarkers.current && waitingMarkers.current[activeRide]) {
      waitingMarkers.current[activeRide].forEach((marker: google.maps.Marker) => {
        if (
          marker.getMap() &&
          (marker.getIcon() as google.maps.Icon).url.search('RidePositionMarker')
        ) {
          marker.setMap(null);
        }
      });
    }
  };

  /**
   * Hide all waiting markers
   * @returns {void}
   */
  const hideAllWaitingMarkers = (): void => {
    Object.keys(waitingMarkers.current).forEach((rideNumber: string) => {
      waitingMarkers.current[Number(rideNumber)].forEach((positionMarker: google.maps.Marker) => {
        if (positionMarker.getMap()) {
          positionMarker.setMap(null);
        }
      });
    });
  };

  /**
   * Show all waiting markers
   * @param {number} rideNumber Show all markers only for specified rideNumber
   * @returns {void}
   */
  const showAllWaitingMarkers = React.useCallback(
    (rideNumber?: number): void => {
      if (rideNumber) {
        Object.keys(waitingMarkers.current).forEach((rideNum: string) => {
          if (Number(rideNum) === rideNumber) {
            waitingMarkers.current[Number(rideNum)].forEach(
              (positionMarker: google.maps.Marker) => {
                if (!positionMarker.getMap()) {
                  positionMarker.setMap(map);
                }
              }
            );
          }
        });
      } else {
        Object.keys(waitingMarkers.current).forEach((rideNum: string) => {
          waitingMarkers.current[Number(rideNum)].forEach((positionMarker: google.maps.Marker) => {
            if (!positionMarker.getMap()) {
              positionMarker.setMap(map);
            }
          });
        });
      }
    },
    [map]
  );

  /**
   * Toggle marker tooltip handler
   * @param {number} rideNumber
   * @param {number{ positionIndex
   * @returns {void}
   */
  const handleToggleTooltip = (rideNumber: number, positionIndex: number): void => {
    if (
      waitingMarkerTooltips.current[rideNumber] &&
      waitingMarkerTooltips.current[rideNumber][positionIndex]
    ) {
      const markerTooltip = waitingMarkerTooltips.current[rideNumber][positionIndex];
      hideAllTooltips(waitingMarkerTooltips, positionIndex);
      if (markerTooltip) {
        markerTooltip.setMap(markerTooltip.getMap() ? null : map);
      }
    }
  };

  // Handle show/hide waiting markers by selected rides
  React.useEffect(() => {
    Object.keys(waitingMarkers.current).forEach((rideNumber: string) => {
      waitingMarkers.current[Number(rideNumber)].forEach((marker: google.maps.Marker) => {
        // if (!selectedRides.includes(Number(rideNumber)) && marker.getMap()) {
        if (!selectedRides.includes(Number(rideNumber)) && marker.getMap()) {
          // console.info('Hide selected waiting markers: ', rideNumber);
          marker.setMap(null);
        }
        if (
          // selectedRides.includes(Number(rideNumber)) &&
          selectedRides.includes(Number(rideNumber)) &&
          !marker.getMap() &&
          zoomLevel >= RIDE_DETAIL_ZOOM_LEVEL
        ) {
          // console.info('Show selected waiting markers: ', rideNumber);
          marker.setMap(map);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedRides]);

  React.useEffect(() => {
    // Create markers for positions
    waitingMarkers.current = createWaitingMarkers(map, rides, activeRide, handleToggleTooltip);

    waitingMarkerTooltips.current = createWaitingMarkerTooltips(rides, intl);

    // Unset everything from map
    return () => {
      hideAllWaitingMarkers();
      waitingMarkers.current = { 1: [] };
      waitingMarkerTooltips.current = { 1: {} };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If enable warnings changes, toggle waiting markers
  React.useEffect(() => {
    if (enableWaitings) {
      showAllWaitingMarkers();
    } else {
      hideAllWaitingMarkers();
    }
  }, [enableWaitings, showAllWaitingMarkers]);

  const data: IUseWaitingMarker = {
    waitingMarkers: waitingMarkers.current,
    showHiddenWaitingMarkers,
    hideShowedWaitingMarkers,
    showAllWaitingMarkers,
    hideAllWaitingMarkers,
  };

  return data;
};

export default useWaitingMarker;
